import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, VStack, Spinner, Text } from '@chakra-ui/react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import SignIn from './components/SignIn';
import CreateUser from './components/CreateUser';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import StartSimulation from './components/StartSimulation';
import Jobs from './components/Jobs';
import Settings from './components/Settings';
import ProtectedRoute from './components/ProtectedRoute';
import TermsOfService from './components/TermsOfService';
import DataPrivacy from './components/DataPrivacy';
import { AuthProvider, useAuth } from './context/AuthContext';
import ResetPassword from './components/ResetPassword';
import BuyCredits from './components/BuyCredits';
// Copilot temporarily hidden — functionality still in development
// import Copilot from './components/Copilot';
// import CopilotIntro from './components/CopilotIntro';
import FAQ from './components/FAQ';
import Pricing from './components/Pricing';
import Tutorial from './components/Tutorial';
import LearningGuide from './components/LearningGuide';
import MultiphaseAcousticsBubbleScreen from './components/blogs/MultiphaseAcousticsBubbleScreen';
import ShockWaterCavityInteraction from './components/blogs/ShockWaterCavityInteraction';
import GreshoVortexValidation from './components/blogs/GreshoVortexValidation';
import TaylorGreenVortexTurbulence from './components/blogs/TaylorGreenVortexTurbulence';
import SupersonicWedgeFlow from './components/blogs/SupersonicWedgeFlow';

import ShockBubbleInteraction from './components/blogs/ShockBubbleInteraction';
import Examples from './components/Examples';
import SimulationViewer from './components/SimulationViewer';
// import CopilotUsage from './components/CopilotUsage';
import businessCaseRoutes from './components/business_cases/routes';
import CookieConsent from './components/CookieConsent';
import CookieBanner from './components/CookieBanner';
import UnderConstructionBanner from './components/UnderConstructionBanner';
import ExecutiveBrief from './components/ExecutiveBrief';

import MaintenancePage from './components/MaintenancePage';

export const UNDER_CONSTRUCTION = false;
export const ALLOWED_EMAIL = '';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Authenticated users are sent to /buy-credits; /pricing is for guests only.
const PricingRoute = () => {
  const { user } = useAuth();
  if (user) {
    return <Navigate to="/buy-credits" replace />;
  }
  return <Pricing />;
};

// Set to true when you want to show maintenance page
const MAINTENANCE_MODE = false;


const App = () => {
  if (MAINTENANCE_MODE) {
    return (
      <AuthProvider>
        <MaintenancePage />
      </AuthProvider>
    );
  }


  return (
    <HelmetProvider>
    <AuthProvider>
      <Helmet>
        <title>SRS | Scale-Resolving Simulation in the Cloud</title>
        <meta name="description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
        <meta name="keywords" content="SRS, scale-resolving simulation, LES, DNS, turbulent flow, GPU simulation, cloud computing, AI simulation, PyFR, flux reconstruction, high-order, CFD, fluid dynamics, scientific computing, HPC, NVIDIA A100, industrial flow" />
        {/* canonical + og:url are set per-page (LandingPage owns "/") so
            pages without their own Helmet never claim the homepage canonical */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SRS | Scale-Resolving Simulation in the Cloud" />
        <meta property="og:description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
        <meta property="og:image" content="https://srs.numericalai.net/og-card.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="SRS — Scale-Resolving Simulation in the Cloud, powered by PyFR" />
        <meta property="og:site_name" content="SRS" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SRS | Scale-Resolving Simulation in the Cloud" />
        <meta name="twitter:description" content="Run GPU-accelerated Scale-Resolving Simulations of turbulent industrial flows in the cloud with PyFR. AI-assisted validation and real-time visualization." />
        <meta name="twitter:image" content="https://srs.numericalai.net/og-card.png" />
      </Helmet>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/jobs" element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/start-simulation" element={
            <ProtectedRoute>
              <StartSimulation />
            </ProtectedRoute>
          } />
          <Route path="/viewer/:jobId" element={
            <ProtectedRoute>
              <SimulationViewer />
            </ProtectedRoute>
          } />
          {/* Copilot routes temporarily hidden — functionality still in development
          <Route path="/copilot" element={
            <ProtectedRoute>
              <Copilot />
            </ProtectedRoute>
          } />
          <Route path="/copilot-usage" element={
            <ProtectedRoute>
              <CopilotUsage />
            </ProtectedRoute>
          } />
          <Route path="/copilot-intro" element={<CopilotIntro />} />
          */}
          <Route path="/tutorial" element={<Tutorial />} />
          <Route path="/learning-guide" element={<LearningGuide />} />
          <Route path="/examples" element={<Examples />} />
          <Route path="/blog/multiphase-acoustics-bubble-screen" element={<MultiphaseAcousticsBubbleScreen />} />
          <Route path="/blog/shock-water-cavity-interaction" element={<ShockWaterCavityInteraction />} />
          <Route path="/blog/taylor-green-vortex-turbulence" element={<TaylorGreenVortexTurbulence />} />
          <Route path="/blog/gresho-vortex-validation" element={<GreshoVortexValidation />} />
          <Route path="/blog/supersonic-wedge-flow" element={<SupersonicWedgeFlow />} />
          <Route path="/blog/shock-bubble-interaction" element={<ShockBubbleInteraction />} />
          {businessCaseRoutes.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={
              <Suspense fallback={<Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center"><VStack spacing={4}><Spinner size="xl" color="brand.500" thickness="4px" /><Text color="gray.600">Loading...</Text></VStack></Box>}>
                <Component />
              </Suspense>
            } />
          ))}

          <Route path="/buy-credits" element={<BuyCredits />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/create-user" element={<CreateUser />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<DataPrivacy />} />
          <Route path="/pricing" element={<PricingRoute />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/executive-brief" element={<ExecutiveBrief />} />
          <Route path="/cookies" element={<CookieConsent />} />          
        </Routes>
        <CookieBanner />
        {UNDER_CONSTRUCTION && <UnderConstructionBanner />}
      </Router>
    </AuthProvider>
    </HelmetProvider>
  );
};

export default App; 